import { React, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate  } from 'react-router-dom';
import _ from 'lodash';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LicenseList from './components/pages/LicenseList';
import { ThemeProvider, createTheme, Backdrop, CircularProgress, Box, Typography, Button } from '@mui/material';
import LicenseDetails from './components/pages/LicenseDetails';
import Banner from './components/common/Banner';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2d186c',
      contrastText: '#fff',
    },
    background: {
      default: '#fff',
      secondary: '#f5f0fa',
    },
    text: {
      primary: '#222',
      secondary: '#888',
    },
    error: {
      main: '#b00020',
    },
    success: {
      main: '#2e7d32',
    },
  },
  typography: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
});

const App = () => {

  const [authenticated, setAuthenticated] = useState(false);
  const [authData, setAuthData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);


  const getEnv = (url) => {
    const envs = {
      'dev': 'https://licensemanager-dev.pearson.com/',
      'qa': 'https://licensemanager-qa.pearson.com/',
      'stg': 'https://licensemanager-stg.pearson.com/',
      'ppe': 'https://licensemanager-ppe.pearson.com/',
      'prod': 'https://licensemanager.pearson.com/',
      'local': 'https://local.pearson.com:3000/',
    }
    let ret = (_.invert(envs))[url];
    return ret;
  }

  const env = getEnv(window.location.origin + '/');
  const authorizedSession = window.piSession;

  const hasPermission = (requiredPermission) => {
    return authData?.permission?.includes(requiredPermission);
  };

  function DoAuthorization() {
    useEffect(() => {

      setLoading(true);
      // Load session.js from whatever environment we accessed the license manager app
      var session_js = document.createElement('script');
      var serverRoot = 'https://login-qa.pearson.com/v1/piapi-test/login';
      var clientId = 'HZl5iDGw8CmCmiAL9SXRb6yhBdou55Da';

      if (env.includes('stg')) {
        serverRoot = 'https://login-stg.pearson.com/v1/piapi-int/login';
      }
      else if (env.includes('ppe')) {
        serverRoot = 'https://login-ppe.pearson.com/v1/piapi-pqa/login';
      }
      else if (env.includes('prod')) {
        serverRoot = 'https://login.pearson.com/v1/piapi/login';
        clientId = 'vI951lgBniyp342MTEcU5INkpWxBcPZ8';
      }

      serverRoot += '/js/v2/session.js';

      session_js.onload = function() {

         // Hang piSession where we can find it in the services layer
         var piSession = window.piSession;

         // Initialize session.js, supplying client ID
         piSession.initialize(clientId, {
           requireLogin: true,
           monitorUserActivity: true,
           useDefaultIdleTimoutWarningPopup: true,
           autoRefresh: true,
         });
 
         piSession.login(window.location.href, 0, function () {
          if (piSession.Success) {
            setAuthenticated(true);

            // Retrieve the token and set it in Axios headers
            piSession.getToken((status, token) => {
              if (status === "success" && token) {
                axios.defaults.headers.common["X-Authorization"] = token;
              // Check user permissions via authorization endpoint
                axios.get('/api/authorization/')
                  .then(response => {
                    // Check if user has any permissions
                    if (response.data && 
                        response.data.authorized === true &&
                        response.data.permission && 
                        response.data.permission.length > 0) {
                      setAuthenticated(true);
                      setAuthData(response.data);
                      setLoading(false);
                    } else {
                      setLoading(false);
                      setError("You don't have sufficient permissions to access this application.");
                    }
                  })
                  .catch(err => {
                    setLoading(false);
                    if (err.response) {
                      setError(err?.response?.data?.message ? err.response.data.message : "An error occurred while verifying your permissions. Please try again later.");
                    } else {
                      setError("An unexpected error occurred while logging in. Please try again later.");
                    }
                  });
              }
            });
          } else {
            setError("Authentication failed. Please try again.");
            setLoading(false);
          }
        });
      };

      session_js.setAttribute('src', serverRoot);
      document.head.appendChild(session_js);

      }, []);
      return <input type='hidden' id='pi_user_id'/>
    }

    const handleLogOut = () => {
      setAuthenticated(false);
      setAuthData({});
      setError(null);
      delete axios.defaults.headers.common["X-Authorization"];
      authorizedSession.clearLocalStorage();
      authorizedSession.logout();
    }

    return (
      <div>
        {loading ? (
          // Show loading indicator while authentication is in progress
          <Backdrop
            open={true}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              color: "common.white",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>) : ''}
        <ThemeProvider theme={theme}>
          {DoAuthorization()}
          {error ? (
          // Error display when user doesn't have permissions
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100vh',
            p: 3
          }}>
            <Typography 
              variant="h5" 
              color="error.main" 
              sx={{ 
                mb: 2, 
                textAlign: 'center',
                fontWeight: 500
              }}
            >
              {error}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                mb: 3,
                maxWidth: 600
              }}
            >
              If you believe this is an error, please contact support.
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={() => handleLogOut()}
            >
              Log Out
            </Button>
          </Box>
        ) : authenticated ? (
          <Router>
            <Banner handleLogOut={handleLogOut} />
            <Routes>
              <Route path="/" element={
                 loading ? '' : (
                    hasPermission('read-license') ? 
                      <LicenseList authData={authData} /> : 
                      <Navigate to="/" replace />
                  )
                } 
              />
              <Route path="/license/:id" element={
                 loading ? '' : (
                    hasPermission('read-license') ? 
                      <LicenseDetails authData={authData} /> : 
                      <Navigate to="/" replace />
                  )
                } 
              />
            </Routes>
          </Router>
        ) : ''}
       </ThemeProvider>
      </div>
    )
}

export default App;
